import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  Stack,
  IconButton,
  Button,
  Checkbox,
  Skeleton,
  Badge,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { useStoreManagement, Store } from '../../context/StoreManagementContext';
import { useTimeFormat } from '../../context/TimeFormatContext';

interface StoreGridViewProps {
  stores: Store[];
  onStoreClick?: (store: Store) => void;
  isLoading?: boolean;
}

export const StoreGridView: React.FC<StoreGridViewProps> = ({
  stores,
  onStoreClick,
  isLoading = false,
}) => {
  const {
    selectedStoreIds,
    toggleStoreSelection,
  } = useStoreManagement();

  const { use24HourFormat } = useTimeFormat();
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const handleToggleExpand = (storeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeId)) {
        newSet.delete(storeId);
      } else {
        newSet.add(storeId);
      }
      return newSet;
    });
  };

  const formatTodayHours = (store: Store) => {
    // Get today's day of week in ISO 8601 format
    // JavaScript: 0 = Sunday, 1 = Monday, ... 6 = Saturday
    // ISO 8601:   1 = Monday, ... 6 = Saturday, 7 = Sunday
    const jsDay = new Date().getDay();
    const today = jsDay === 0 ? 7 : jsDay;

    // Find occasions that are enabled (businessHours from Byte Graph)
    const occasions = store.hours as any[] || [];

    // Look for any enabled occasion to get hours
    const enabledOccasions = occasions.filter((occ: any) => occ.isEnabled);

    if (enabledOccasions.length === 0) {
      return 'Hours not available';
    }

    // Try to find hours for today from any occasion
    for (const occasion of enabledOccasions) {
      if (!occasion.hours || occasion.hours.length === 0) continue;

      for (const hour of occasion.hours) {
        if (hour.timeFrame && hour.timeFrame.days && hour.timeFrame.days.includes(today)) {
          // Skip special hours (those with date range)
          if (hour.timeFrame.startDate && hour.timeFrame.endDate) {
            continue;
          }

          // Parse the interval start time and duration
          const intervalStart = hour.timeFrame.intervalStartTime || '00:00';
          const durationStr = hour.timeFrame.duration || '0';

          // Parse duration from HH:MM:SS or HH:MM format to minutes
          let duration = 0;
          if (typeof durationStr === 'string' && durationStr.includes(':')) {
            const parts = durationStr.split(':').map(Number);
            if (parts.length >= 2) {
              duration = parts[0] * 60 + parts[1];
            }
          } else {
            duration = Number(durationStr) || 0;
          }

          // Calculate start time in minutes
          const [startHour, startMin] = intervalStart.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;

          // Add duration to get end time
          const endMinutes = startMinutes + duration;

          // Convert back to hours and minutes
          const endHour = Math.floor(endMinutes / 60) % 24;
          const endMin = endMinutes % 60;

          if (use24HourFormat) {
            const startStr = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
            const endStr = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
            return `${startStr} - ${endStr}`;
          } else {
            const formatTime = (hour: number, min: number) => {
              const period = hour >= 12 ? 'PM' : 'AM';
              const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
              return `${displayHour}:${String(min).padStart(2, '0')} ${period}`;
            };
            return `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`;
          }
        }
      }
    }

    return 'Closed today';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'error': return 'error';
      default: return 'success';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active': return <ActiveIcon fontSize="small" />;
      case 'inactive': return <InactiveIcon fontSize="small" />;
      case 'error': return <WarningIcon fontSize="small" />;
      default: return <ActiveIcon fontSize="small" />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={280} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        {stores.map((store) => {
          const isSelected = selectedStoreIds.has(store.storeNumber);
          const isExpanded = expandedCards.has(store.storeNumber);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={store.storeNumber}>
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Selection Checkbox */}
                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleStoreSelection(store.storeNumber);
                    }}
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                    }}
                  />
                </Box>

                <CardContent sx={{ pt: 5, flexGrow: 1 }}>
                  {/* Store Header */}
                  <Box
                    onClick={() => onStoreClick?.(store)}
                    sx={{ cursor: 'pointer', mb: 2 }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {store.storeNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {store.storeName}
                    </Typography>
                  </Box>

                  {/* Today's Hours */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Today's Hours
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {formatTodayHours(store)}
                    </Typography>
                  </Box>

                  {/* Channel Status */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Channel Status
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(() => {
                        const chips: JSX.Element[] = [];

                        // Add Web/App chip if online orders enabled
                        if (store.onlineOrders) {
                          chips.push(
                            <Chip
                              key="web-app"
                              label="Web/App"
                              size="small"
                              color="success"
                              variant="filled"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          );
                        }

                        // Add delivery channel chips
                        const channels = store.channels || store.activeChannels || [];
                        const activeChannels = channels.filter(ch => ch.status === 'ONLINE' || ch.status === 'ACTIVE');

                        activeChannels.forEach((ch, idx) => {
                          let label = ch.channel;
                          if (ch.channel === 'UBEREATS') label = 'Uber Eats';
                          else if (ch.channel === 'DOORDASH') label = 'DoorDash';
                          else if (ch.channel === 'GRUBHUB') label = 'Grubhub';
                          else if (ch.channel === 'DELIVEROO') label = 'Deliveroo';
                          else if (ch.channel === 'JUST_EAT') label = 'Just Eat';

                          chips.push(
                            <Chip
                              key={`${ch.channel}-${idx}`}
                              label={label}
                              size="small"
                              color="success"
                              variant="filled"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          );
                        });

                        if (chips.length === 0) {
                          return (
                            <Typography variant="caption" color="text.secondary">
                              No active channels
                            </Typography>
                          );
                        }

                        return chips;
                      })()}
                    </Stack>
                  </Box>

                  {/* Last Modified */}
                  <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <UpdateIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {store.lastModified?.timestamp
                          ? `Updated ${new Date(store.lastModified.timestamp).toLocaleDateString('en-GB', {
                              month: 'short',
                              day: 'numeric'
                            })}`
                          : 'No recent changes'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      {/* Channel Status */}
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Channel Status
                      </Typography>
                      <Stack spacing={0.5} sx={{ mb: 2 }}>
                        {/* Web/App status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Web/App:
                          </Typography>
                          <Typography variant="caption" color={store.onlineOrders ? 'success.main' : 'text.disabled'}>
                            {store.onlineOrders ? 'ENABLED' : 'DISABLED'}
                          </Typography>
                        </Box>
                        {/* Delivery channels */}
                        {(() => {
                          const channels = store.channels || store.activeChannels || [];
                          return channels.map((ch, idx) => {
                            let label = ch.channel;
                            if (ch.channel === 'UBEREATS') label = 'Uber Eats';
                            else if (ch.channel === 'DOORDASH') label = 'DoorDash';
                            else if (ch.channel === 'GRUBHUB') label = 'Grubhub';
                            else if (ch.channel === 'DELIVEROO') label = 'Deliveroo';
                            else if (ch.channel === 'JUST_EAT') label = 'Just Eat';

                            const isActive = ch.status === 'ONLINE' || ch.status === 'ACTIVE';
                            return (
                              <Box key={`${ch.channel}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {label}:
                                </Typography>
                                <Typography variant="caption" color={isActive ? 'success.main' : 'text.disabled'}>
                                  {ch.status}
                                </Typography>
                              </Box>
                            );
                          });
                        })()}
                      </Stack>
                    </Box>
                  )}
                </CardContent>

                <Divider />

                {/* Card Actions */}
                <CardActions sx={{ px: 2, py: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStoreClick?.(store);
                    }}
                  >
                    Manage
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggleExpand(store.storeNumber, e)}
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                    />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};